import { Dispatch } from 'react';
import Cookies from 'js-cookie';
import { axiosApiInstance } from 'Store';
import { userApiRequests } from 'utils/api-requests/user';
import { decodeJwtToken } from 'utils/decodeJwtToken';
import { Platform, usePlatform } from 'utils/hooks/usePlatform';
import { isAllowedDomain } from 'utils/isAllowedDomain.ts';
import { UserReactions } from 'utils/types/OfferData';
import { UserOfferRequestData, V2OfferType } from 'utils/types/UsersAdvert';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

import { Action } from './Reducer';
import { MessagesAdvertType } from './State';

const platform = usePlatform();

const loadMyOffers = async (dispatch: Dispatch<Action>, offerType: V2OfferType, page: number, size: number): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_MY_OFFERS',
        payload: await userApiRequests.getMyOffers(offerType, page, size)
    });
};

const logInUser = async (dispatch: Dispatch<Action>, login: string, password: string, isLoginForRegistration = false): Promise<void | boolean> => {
    dispatch({ type: 'SET_LOADING' });
    const loggedUser = await userApiRequests.authenticate(login, password);

    if (loggedUser && typeof loggedUser === 'object') {
        if (!isLoginForRegistration) {
            activeNotification(
                'Zrobione!',
                'Pomyślnie zalogowano',
                'success'
            );
        }
    }

    if (loggedUser && typeof loggedUser === 'object') {
        if (!isAllowedDomain(window.location.hostname)) {
            Cookies.set('MonitorApiToken', loggedUser.token, { domain: '.investoro.pl' });
            Cookies.set('MonitorApiRefresh', loggedUser.refreshToken, { domain: '.investoro.pl' });
            Cookies.set('MonitorRoles', loggedUser.roles, { domain: '.investoro.pl' });
        }

        if (platform === Platform.IOS || platform === Platform.Android) {
            localStorage.setItem('MonitorApiToken', loggedUser.token);
            localStorage.setItem('MonitorApiRefresh', loggedUser.refreshToken);
            localStorage.setItem('MonitorRoles', loggedUser.roles);
        }

        if (isAllowedDomain(window.location.hostname)) {
            Cookies.set('MonitorApiToken', loggedUser.token, { path: '/' });
            Cookies.set('MonitorApiRefresh', loggedUser.refreshToken, { path: '/' });
            Cookies.set('MonitorRoles', loggedUser.roles, { path: '/' });
        }

        const decodedToken = decodeJwtToken(loggedUser.token);

        dispatch({
            type: 'LOGIN',
            payload: {
                ...loggedUser,
                organization: decodedToken?.organization || false,
                organizationRole: decodedToken?.organizationRole || '',
                isWordpressUser: decodedToken?.isWordpressUser || false,
                userRole: decodedToken?.userRole || '',
                activePackages: loggedUser?.roles || []
            }
        });

        if (isLoginForRegistration) {
            await new Promise((resolve) => setTimeout(resolve, 200));
            dispatch({ type: 'SET_LOADING', payload: false });

            return true;
        }
    }

    if (isLoginForRegistration) {
        dispatch({ type: 'SET_LOADING', payload: false });

        return false;
    }

    dispatch({ type: 'SET_LOADING', payload: false });
};

const loginByToken = async (dispatch: Dispatch<Action>, isLoginForPurchaseApp = false): Promise<void | boolean> => {
    let apiToken = Cookies.get('MonitorApiToken');
    let refreshToken = Cookies.get('MonitorApiRefresh');
    let roles = Cookies.get('MonitorRoles')?.split(',');

    if (platform === Platform.IOS || platform === Platform.Android) {
        if (!apiToken) apiToken = localStorage.getItem('MonitorApiToken') || undefined;
        if (!refreshToken) refreshToken = localStorage.getItem('MonitorApiRefresh') || undefined;

        if (!roles) {
            const localRoles = localStorage.getItem('MonitorRoles');
            roles = localRoles ? localRoles.split(',') : undefined;
        }
    }

    if (apiToken && refreshToken) {
        const decodedToken = decodeJwtToken(apiToken);

        if (decodedToken) {
            const rolesFromToken = decodedToken.roles || decodedToken.userRole || '';

            dispatch({
                type: 'LOGIN',
                payload: {
                    token: apiToken,
                    refreshToken,
                    roles: rolesFromToken,
                    user: decodedToken.user || '',
                    organization: decodedToken?.organization || false,
                    organizationRole: decodedToken?.organizationRole || '',
                    isWordpressUser: decodedToken?.isWordpressUser || false,
                    userRole: decodedToken?.userRole || '',
                    activePackages: roles
                }
            });

            if (isLoginForPurchaseApp) {
                return true;
            }
        } else {
            const errorId = v4();
            const error = new Error('Nie udało się zdekodować tokena');
            userApiRequests.logException(error, errorId);
        }
    } else {
        const errorId = v4();
        const error = new Error(`Brak tokenów: apiToken: ${!!apiToken}, refreshToken: ${!!refreshToken}`);
        userApiRequests.logException(error, errorId);
    }

    if (isLoginForPurchaseApp) {
        return false;
    }

    dispatch({ type: 'SET_LOADING', payload: false });
};

const refreshUserToken = async (dispatch: Dispatch<Action>): Promise<boolean> => {
    try {
        let refreshToken = Cookies.get('MonitorApiRefresh');

        if (platform === Platform.IOS || platform === Platform.Android) {
            if (!refreshToken) refreshToken = localStorage.getItem('MonitorApiRefresh') || undefined;
        }

        if (!refreshToken) {
            return false;
        }

        const refreshedUser = await userApiRequests.authenticateByToken(refreshToken);

        if (refreshedUser && typeof refreshedUser === 'object') {
            if (!isAllowedDomain(window.location.hostname)) {
                Cookies.set('MonitorApiToken', refreshedUser.token, { domain: '.investoro.pl' });
                Cookies.set('MonitorApiRefresh', refreshedUser.refreshToken, { domain: '.investoro.pl' });
                Cookies.set('MonitorRoles', refreshedUser.roles, { domain: '.investoro.pl' });
            }

            if (platform === Platform.IOS || platform === Platform.Android) {
                localStorage.setItem('MonitorApiToken', refreshedUser.token);
                localStorage.setItem('MonitorApiRefresh', refreshedUser.refreshToken);
                localStorage.setItem('MonitorRoles', refreshedUser.roles);
            }

            if (isAllowedDomain(window.location.hostname)) {
                Cookies.set('MonitorApiToken', refreshedUser.token, { path: '/' });
                Cookies.set('MonitorApiRefresh', refreshedUser.refreshToken, { path: '/' });
                Cookies.set('MonitorRoles', refreshedUser.roles, { path: '/' });
            }

            axiosApiInstance.defaults.headers.common = { Authorization: `Bearer ${refreshedUser.token}` };
            const decodedToken = decodeJwtToken(refreshedUser.token);

            if (decodedToken) {
                dispatch({
                    type: 'LOGIN',
                    payload: {
                        token: refreshedUser.token,
                        refreshToken: refreshedUser.refreshToken,
                        roles: decodedToken.userRole || '',
                        user: decodedToken.user || '',
                        organization: decodedToken?.organization || false,
                        organizationRole: decodedToken?.organizationRole || '',
                        isWordpressUser: decodedToken?.isWordpressUser || false,
                        userRole: decodedToken?.userRole || '',
                        activePackages: refreshedUser?.roles || []
                    }
                });
            }

            return true;
        }

        return false;
    } catch (error) {
        const errorId = v4();
        userApiRequests.logException(error instanceof Error ? error : new Error(String(error)), errorId);

        return false;
    }
};

const logout = async (dispatch: Dispatch<Action>): Promise<void> => {
    localStorage.removeItem('MonitorApiToken');
    localStorage.removeItem('MonitorApiRefreshToken');
    localStorage.removeItem('MonitorRoles');
    localStorage.removeItem('smsInfoShown');
    localStorage.removeItem('tokenInfoShown');

    if (!isAllowedDomain(window.location.hostname)) {
        Cookies.remove('MonitorApiToken', { domain: '.investoro.pl' });
        Cookies.remove('MonitorApiRefresh', { domain: '.investoro.pl' });
        Cookies.remove('MonitorRoles', { domain: '.investoro.pl' });
    }

    if (isAllowedDomain(window.location.hostname)) {
        Cookies.remove('MonitorApiToken', { path: '/' });
        Cookies.remove('MonitorApiRefresh', { path: '/' });
        Cookies.remove('MonitorRoles', { path: '/' });
    }

    await userApiRequests.logout();
    dispatch({ type: 'LOGOUT' });
};

const changeTheme = async (dispatch: Dispatch<Action>, isDarkMode: boolean): Promise<void> => {
    localStorage.setItem('darkTheme', isDarkMode.toString());

    dispatch({
        type: 'CHANGE_THEME',
        payload: isDarkMode
    });
};

const changeErrorVisibility = async (dispatch: Dispatch<Action>, error: boolean): Promise<void> => {
    dispatch({
        type: 'CHANGE_ERROR_VISIBILITY',
        payload: error
    });
};

const loadUserOffersAsync = async (dispatch: Dispatch<Action>, page: number, size: number, params?: UserOfferRequestData, offerType?: V2OfferType): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_FILTERS',
        payload: await userApiRequests.getOffers(page, size, params, offerType)
    });
};

const loadOfferDetailsAsync = async (dispatch: Dispatch<Action>, offerType: V2OfferType, id: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_DETAILED_OFFER',
        payload: await userApiRequests.getOfferDetails(offerType, id)
    });
};

const loadMyOfferToEdit = async (dispatch: Dispatch<Action>, offerType: V2OfferType, id: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_DETAILS_TO_EDIT',
        payload: await userApiRequests.getMyOfferDetails(offerType, id)
    });
};

const loadMyOfferDetailsAsync = async (dispatch: Dispatch<Action>, offerType: V2OfferType, id: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_DETAILED_MYOFFER',
        payload: await userApiRequests.getMyOfferDetails(offerType, id)
    });
};

const getOwnerProfile = async (dispatch: Dispatch<Action>, id: number, page: number, size: number): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_PROFILE_OFFERS',
        payload: await userApiRequests.getOwnerProfile(id, page, size)
    });
};

const getProfile = async (dispatch: Dispatch<Action>): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_PROFILE',
        payload: await userApiRequests.getProfile()
    });
};

const getNotificationSettings = async (dispatch: Dispatch<Action>): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_NOTIFICATION_SETTINGS',
        payload: await userApiRequests.getNotificationSettings()
    });
};

const getTokenInfo = async (dispatch: Dispatch<Action>): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_TOKEN_INFO',
        payload: await userApiRequests.getTokenInfo()
    });
};

const getActivePackageInfo = async (dispatch: Dispatch<Action>): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_ACTIVE_PACKAGE_INFO',
        payload: await userApiRequests.getActivePackageInfo()
    });
};

const loadProfileOfferDetailsAsync = async (dispatch: Dispatch<Action>, offerType: V2OfferType, id: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_DETAILED_PROFILE_OFFER',
        payload: await userApiRequests.getOfferDetails(offerType, id)
    });
};

const clearOffers = async (dispatch: Dispatch<Action>): Promise<void> => {
    dispatch({
        type: 'CLEAR'
    });
};

const deleteMyOffer = async (dispatch: Dispatch<Action>, advertisementId: string, offerType: V2OfferType): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    const validChangeStatus = await userApiRequests.deleteMyOffer(advertisementId, offerType);

    if (validChangeStatus) {
        dispatch({
            type: 'DELETE_MY_OFFER',
            payload: advertisementId
        });
    }
};

const reactionUpdate = async (dispatch: Dispatch<Action>, id: string, city: string, reactions: UserReactions, offerType: V2OfferType): Promise<void> => {
    const response = await userApiRequests.setOfferReaction(id, city, reactions, offerType);

    if (response) {
        dispatch({
            type: 'REACTION_UPDATE',
            payload: {
                id, reactions: response
            }
        });
    }
};

const loadNotesAsync = async (dispatch: Dispatch<Action>, offerType: V2OfferType, id: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    dispatch({
        type: 'SET_NOTES',
        payload: {
            id,
            data: await userApiRequests.getNotes(offerType, id)
        }
    });
};

const deleteNote = async (dispatch: Dispatch<Action>, id: string, offerType: V2OfferType, noteId: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    const validChangeStatus = await userApiRequests.deleteNote(offerType, noteId);

    if (validChangeStatus) {
        dispatch({
            type: 'DELETE_NOTE',
            payload: {
                noteId, id
            }
        });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
};

const deleteProfileOffersNote = async (dispatch: Dispatch<Action>, id: string, offerType: V2OfferType, noteId: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    const validChangeStatus = await userApiRequests.deleteNote(offerType, noteId);

    if (validChangeStatus) {
        dispatch({
            type: 'DELETE_PROFILE_OFFERS_NOTE',
            payload: {
                noteId, id
            }
        });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
};

const updateNote = async (dispatch: Dispatch<Action>, id: string, offerType: V2OfferType, note: string, noteId?: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    const validChangeStatus = await userApiRequests.updateNote(offerType, note, id, noteId);

    if (validChangeStatus) {
        if (noteId) {
            dispatch({
                type: 'UPDATE_NOTE',
                payload: {
                    ...validChangeStatus
                }
            });
        } else {
            dispatch({
                type: 'NEW_NOTE',
                payload: {
                    ...validChangeStatus
                }
            });
        }
    }

    dispatch({ type: 'SET_LOADING', payload: false });
};

const updateProfileOffersNote = async (dispatch: Dispatch<Action>, id: string, offerType: V2OfferType, note: string, noteId?: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING' });
    const validChangeStatus = await userApiRequests.updateNote(offerType, note, id, noteId);

    if (validChangeStatus) {
        if (noteId) {
            dispatch({
                type: 'UPDATE_NOTE',
                payload: {
                    ...validChangeStatus
                }
            });
        } else {
            dispatch({
                type: 'NEW_PROFILE_OFFERS_NOTE',
                payload: {
                    ...validChangeStatus
                }
            });
        }
    }
    dispatch({ type: 'SET_LOADING', payload: false });
};

const loadUserMessagesAsync = async (dispatch: Dispatch<Action>, module: MessagesAdvertType): Promise<void> => {
    dispatch({
        type: 'SET_MESSAGES',
        payload: await userApiRequests.getMessages(module)
    });
};

const changeVisibilityOffer = async (dispatch: Dispatch<Action>, module: string, id: string, visibility: boolean): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    await userApiRequests.changeVisibilityOffer(module, id, visibility);
    dispatch({
        type: 'SET_VISIBILITY_OFFER',
        payload: { id, status: visibility }
    });
    dispatch({ type: 'SET_LOADING', payload: false });
};

export const UserActions = {
    logInUser,
    loginByToken,
    logout,
    changeTheme,
    changeErrorVisibility,
    loadUserOffersAsync,
    loadOfferDetailsAsync,
    loadMyOffers,
    loadMyOfferDetailsAsync,
    clearOffers,
    deleteMyOffer,
    reactionUpdate,
    loadNotesAsync,
    updateNote,
    updateProfileOffersNote,
    deleteNote,
    deleteProfileOffersNote,
    getOwnerProfile,
    loadProfileOfferDetailsAsync,
    loadMyOfferToEdit,
    getProfile,
    loadUserMessagesAsync,
    changeVisibilityOffer,
    getNotificationSettings,
    getActivePackageInfo,
    getTokenInfo,
    refreshUserToken
};
